import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStores } from '../../hooks/use-stores';
import services from '../../services';

import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';

import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';

import PrincipalChooser from './PrincipalChooser';
import SimpleTable from '../../components/super/SimpleTable';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import ErrorMessage from '../../components/errormessage';

const TAB_SUBJECTS = 'subjects';
const TAB_TEMPLATES = 'templates';
const TAB_QUESTIONS = 'questions';
const TAB_MATERIALS = 'materials';
const TAB_CATEGORIES = 'categories';

const FILTER_ALL = 'all';
const FILTER_DEFAULT = 'default';
const FILTER_ALLOW = 'allow';
const FILTER_DENY = 'deny';

function ContentGroupDetails() {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { superStore, commonStore } = useStores();
    const {
        aclGroups,
        aclRulesForPrincipal,
        loadingAclRulesForPrincipal,
        creatingAclLine,
        removingAclLines,
        loadingSubjects,
        subjects,
        loadingTemplates,
        templates,
        loadingQuestions,
        questions,
        loadingMaterials,
        materials,
        loadingCategories,
        categories,
    } = superStore;

    const [activeTab, setActiveTab] = useState(TAB_SUBJECTS);
    const [filterByTab, setFilterByTab] = useState({
        [TAB_SUBJECTS]: FILTER_ALL,
        [TAB_TEMPLATES]: FILTER_ALL,
        [TAB_QUESTIONS]: FILTER_ALL,
        [TAB_MATERIALS]: FILTER_ALL,
        [TAB_CATEGORIES]: FILTER_ALL,
    });
    const [deletingGroup, setDeletingGroup] = useState(false);
    const [hasLoadedSubjects, setHasLoadedSubjects] = useState(false);
    const [hasLoadedTemplates, setHasLoadedTemplates] = useState(false);
    const [hasLoadedQuestions, setHasLoadedQuestions] = useState(false);
    const [hasLoadedMaterials, setHasLoadedMaterials] = useState(false);
    const [hasLoadedCategories, setHasLoadedCategories] = useState(false);

    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [savingRename, setSavingRename] = useState(false);

    const [memberTarget, setMemberTarget] = useState(null);
    const [memberTargetName, setMemberTargetName] = useState('');
    const [memberRequiredError, setMemberRequiredError] = useState(null);

    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [selectedContentIds, setSelectedContentIds] = useState(() => new Set());
    const [bulkLoading, setBulkLoading] = useState(false);
    const [addingMember, setAddingMember] = useState(false);

    const group = useMemo(() => {
        if (!groupId || !aclGroups.length) return null;
        return (
            aclGroups.find((g) => g.unique_id === groupId || String(g.id) === String(groupId)) ||
            null
        );
    }, [aclGroups, groupId]);

    useEffect(() => {
        superStore.loadAclGroups();
    }, [superStore]);

    useEffect(() => {
        if (group?.unique_id) {
            superStore.loadAclRulesForPrincipal(group.unique_id);
        }
    }, [group?.unique_id, superStore]);

    const loadMembers = useCallback(() => {
        if (!group?.unique_id) return;
        setLoadingMembers(true);
        services.ACL.getGroupMembers(group.unique_id)
            .then((list) => setMembers(Array.isArray(list) ? list : []))
            .catch(() => setMembers([]))
            .finally(() => setLoadingMembers(false));
    }, [group?.unique_id]);

    useEffect(() => {
        if (!group?.unique_id) return;
        loadMembers();
    }, [group?.unique_id, loadMembers]);

    // Load content for the initially selected tab when group is ready (fix first-load bug)
    useEffect(() => {
        if (!group?.unique_id || activeTab !== TAB_SUBJECTS || hasLoadedSubjects) return;
        superStore.loadSubjects().then(() => setHasLoadedSubjects(true));
    }, [group?.unique_id, activeTab, hasLoadedSubjects]);

    const handleTabChange = async (event, value) => {
        setActiveTab(value);
        switch (value) {
            case TAB_SUBJECTS:
                if (!hasLoadedSubjects) {
                    await superStore.loadSubjects();
                    setHasLoadedSubjects(true);
                }
                break;
            case TAB_TEMPLATES:
                if (!hasLoadedTemplates) {
                    await superStore.loadTemplates();
                    setHasLoadedTemplates(true);
                }
                break;
            case TAB_QUESTIONS:
                if (!hasLoadedQuestions) {
                    await superStore.loadQuestions();
                    setHasLoadedQuestions(true);
                }
                break;
            case TAB_MATERIALS:
                if (!hasLoadedMaterials) {
                    await superStore.loadMaterials();
                    setHasLoadedMaterials(true);
                }
                break;
            case TAB_CATEGORIES:
                if (!hasLoadedCategories) {
                    await superStore.loadCategories();
                    setHasLoadedCategories(true);
                }
                break;
            default:
                break;
        }
    };

    const findRuleForObject = useCallback(
        (objectUniqueId) => {
            if (!group) return null;
            return (
                aclRulesForPrincipal.find(
                    (rule) =>
                        rule.object_unique_id === objectUniqueId &&
                        rule.principal_unique_id === group.unique_id
                ) || null
            );
        },
        [aclRulesForPrincipal, group]
    );

    const getAccessValue = useCallback(
        (objectUniqueId) => {
            if (!objectUniqueId || !group) return FILTER_ALL;
            const rule = findRuleForObject(objectUniqueId);
            if (!rule) return 'default';
            return rule.rule === 1 ? FILTER_ALLOW : FILTER_DENY;
        },
        [findRuleForObject, group]
    );

    const handleFilterChange = (tabKey) => (event) => {
        setFilterByTab((prev) => ({ ...prev, [tabKey]: event.target.value }));
    };

    const handleAccessChange = useCallback(
        async (objectUniqueId, nextValue) => {
            if (!group || !objectUniqueId) return;
            const currentRule = findRuleForObject(objectUniqueId);
            if (nextValue === 'default') {
                if (currentRule) {
                    await superStore.removeAclLine(currentRule.id);
                    await superStore.loadAclRulesForPrincipal(group.unique_id);
                }
                return;
            }
            const desiredRule = nextValue === FILTER_ALLOW ? 1 : 0;
            if (currentRule && currentRule.rule === desiredRule) return;
            try {
                await superStore.createAclLine({
                    rule: desiredRule,
                    principal_unique_id: group.unique_id,
                    object_unique_id: objectUniqueId,
                });
                await superStore.loadAclRulesForPrincipal(group.unique_id);
            } catch (e) {
                commonStore.error('Failed to update access rule');
            }
        },
        [group, findRuleForObject, superStore, commonStore]
    );

    const handleDeleteGroup = () => {
        if (!group) return;
        commonStore.showConfirm(
            `Delete content group "${group.name}"?`,
            'Delete',
            'Delete group',
                async () => {
                    setDeletingGroup(true);
                    try {
                        await services.ACL.deleteGroup(group.unique_id);
                        await superStore.loadAclGroups();
                    navigate('..', { relative: 'path' });
                } catch (e) {
                    commonStore.error('Failed to delete content group');
                } finally {
                    setDeletingGroup(false);
                }
            }
        );
    };

    const handleOpenRenameModal = () => {
        setRenameValue(group?.name || '');
        setRenameModalOpen(true);
    };

    const handleRename = async () => {
        if (!group || !renameValue.trim()) return;
        setSavingRename(true);
        try {
            await services.ACL.updateGroup(group.unique_id, { name: renameValue.trim() });
            await superStore.loadAclGroups();
            setRenameModalOpen(false);
        } catch (e) {
            commonStore.error('Failed to rename group');
        } finally {
            setSavingRename(false);
        }
    };

    const handleAddMember = async () => {
        if (!memberTarget || !group) {
            setMemberRequiredError('Select a partner or company');
            return;
        }
        setMemberRequiredError(null);
        setAddingMember(true);
        try {
            await services.ACL.addGroupMember(group.unique_id, memberTarget.unique_id);
            setMemberTarget(null);
            setMemberTargetName('');
            loadMembers();
        } catch (e) {
            commonStore.error('Failed to add member');
        } finally {
            setAddingMember(false);
        }
    };

    const handleRemoveMember = (member) => {
        const principalId = member.principal_unique_id ?? member.id;
        if (principalId == null) return;
        commonStore.showConfirm(
            `Remove ${member.companyName || member.partnerName || member.name || principalId} from group?`,
            'Remove',
            'Remove member',
            async () => {
                try {
                    await services.ACL.removeGroupMember(group.unique_id, principalId);
                    loadMembers();
                } catch (e) {
                    commonStore.error('Failed to remove member');
                }
            }
        );
    };

    const handleBulkAction = useCallback(
        async (action) => {
            if (!group?.unique_id || selectedContentIds.size === 0) return;
            const ids = Array.from(selectedContentIds);
            setBulkLoading(true);
            try {
                if (action === 'reset') {
                    await services.ACL.removeContentAccessBulk(group.unique_id, ids);
                } else {
                    const rule = action === 'allow' ? 1 : 0;
                    await services.ACL.setContentAccessBulk(group.unique_id, ids, rule);
                }
                setSelectedContentIds(new Set());
                await superStore.loadAclRulesForPrincipal(group.unique_id);
            } catch (e) {
                commonStore.error('Failed to update access');
            } finally {
                setBulkLoading(false);
            }
        },
        [group?.unique_id, selectedContentIds, superStore, commonStore]
    );

    const toggleContentSelection = useCallback((objectUniqueId) => {
        if (objectUniqueId == null) return;
        setSelectedContentIds((prev) => {
            const next = new Set(prev);
            if (next.has(objectUniqueId)) next.delete(objectUniqueId);
            else next.add(objectUniqueId);
            return next;
        });
    }, []);

    const toggleSelectAllContent = useCallback(() => {
        const currentRows = buildRowsForActiveTab();
        const ids = currentRows.map((row) => getRowUniqueId(row)).filter(Boolean);
        setSelectedContentIds((prev) => {
            const next = new Set(prev);
            const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
            if (allSelected) ids.forEach((id) => next.delete(id));
            else ids.forEach((id) => next.add(id));
            return next;
        });
    }, [activeTab, subjects, templates, questions, materials, categories, filterByTab]);

    const isBusy =
        creatingAclLine || removingAclLines.length > 0 || loadingAclRulesForPrincipal;

    const getRowUniqueId = useCallback((row) => {
        return row.unique_id != null ? row.unique_id : null;
    }, []);

    const buildColumnsForType = (type) => {
        const accessAccessor = (row) => {
            const objectUniqueId = getRowUniqueId(row);
            if (objectUniqueId == null) return null;
            const value = getAccessValue(objectUniqueId);
            return (
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={value}
                    onChange={(event, newValue) => {
                        if (newValue != null && newValue !== value) {
                            handleAccessChange(objectUniqueId, newValue);
                        }
                    }}
                    disabled={isBusy}
                >
                    <ToggleButton
                        value="default"
                        sx={{ '&.Mui-selected': { backgroundColor: 'rgba(0,0,0,0.08)' } }}
                    >
                        Default
                    </ToggleButton>
                    <ToggleButton
                        value={FILTER_ALLOW}
                        color="success"
                        sx={{ '&.Mui-selected': { backgroundColor: 'rgba(46, 125, 50, 0.2)' } }}
                    >
                        Allow
                    </ToggleButton>
                    <ToggleButton
                        value={FILTER_DENY}
                        color="error"
                        sx={{ '&.Mui-selected': { backgroundColor: 'rgba(211, 47, 47, 0.2)' } }}
                    >
                        Deny
                    </ToggleButton>
                </ToggleButtonGroup>
            );
        };

        const baseCols = (idKey, nameKey) => [
            { Header: 'Id', accessor: idKey },
            { Header: 'Name', accessor: nameKey },
            { Header: 'Access', id: 'access', accessor: (row) => accessAccessor(row) },
        ];

        switch (type) {
            case TAB_SUBJECTS:
                return baseCols('subject_id', 'title');
            case TAB_TEMPLATES:
                return baseCols('id', 'title');
            case TAB_QUESTIONS:
                return baseCols('id', 'name');
            case TAB_MATERIALS:
                return baseCols('id', 'title');
            case TAB_CATEGORIES:
                return baseCols('id', 'name');
            default:
                return baseCols('id', 'name');
        }
    };

    const buildRowsForActiveTab = () => {
        const currentFilter = filterByTab[activeTab] || FILTER_ALL;
        const applyFilter = (rows, getUniqueId) => {
            if (currentFilter === FILTER_ALL) return rows;
            return rows.filter((row) => {
                const access = getAccessValue(getUniqueId(row));
                if (currentFilter === FILTER_DEFAULT) return access === 'default';
                if (currentFilter === FILTER_ALLOW) return access === FILTER_ALLOW;
                if (currentFilter === FILTER_DENY) return access === FILTER_DENY;
                return true;
            });
        };

        const withUid = (arr, getUid) => (arr || []).map((r) => ({ ...r, unique_id: getUid(r) }));

        switch (activeTab) {
            case TAB_SUBJECTS:
                return applyFilter(withUid(subjects, (r) => r.unique_id), (row) => getRowUniqueId(row));
            case TAB_TEMPLATES:
                return applyFilter(withUid(templates, (r) => r.unique_id), (row) => getRowUniqueId(row));
            case TAB_QUESTIONS:
                return applyFilter(withUid(questions, (r) => r.unique_id), (row) => getRowUniqueId(row));
            case TAB_MATERIALS:
                return applyFilter(withUid(materials, (r) => r.unique_id), (row) => getRowUniqueId(row));
            case TAB_CATEGORIES:
                return applyFilter(withUid(categories, (r) => r.unique_id), (row) => getRowUniqueId(row));
            default:
                return [];
        }
    };

    const rows = buildRowsForActiveTab();
    const allRowIds = useMemo(
        () => rows.map((row) => getRowUniqueId(row)).filter(Boolean),
        [rows]
    );
    const allSelected = allRowIds.length > 0 && allRowIds.every((id) => selectedContentIds.has(id));
    const someSelected = allRowIds.some((id) => selectedContentIds.has(id));

    const columns = useMemo(() => {
        const baseCols = buildColumnsForType(activeTab);
        const selectColumn = {
            id: 'select',
            Header: () => (
                <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={toggleSelectAllContent}
                    disabled={rows.length === 0}
                />
            ),
            accessor: (row) => {
                const id = getRowUniqueId(row);
                return (
                    <Checkbox
                        checked={id != null && selectedContentIds.has(id)}
                        onChange={() => toggleContentSelection(id)}
                        disabled={id == null}
                    />
                );
            },
        };
        return [selectColumn, ...baseCols];
    }, [activeTab, group, aclRulesForPrincipal, isBusy, selectedContentIds, allSelected, someSelected, rows.length]);
    const currentFilter = filterByTab[activeTab] || FILTER_ALL;

    const isTabLoading =
        (activeTab === TAB_SUBJECTS && loadingSubjects && !hasLoadedSubjects) ||
        (activeTab === TAB_TEMPLATES && loadingTemplates && !hasLoadedTemplates) ||
        (activeTab === TAB_QUESTIONS && loadingQuestions && !hasLoadedQuestions) ||
        (activeTab === TAB_MATERIALS && loadingMaterials && !hasLoadedMaterials) ||
        (activeTab === TAB_CATEGORIES && loadingCategories && !hasLoadedCategories);

    const memberColumns = useMemo(
        () => [
            {
                Header: 'Member',
                accessor: (x) =>
                    x.companyName != null || x.partnerName != null
                        ? `${x.companyName ? 'Company: ' : 'Partner: '}${x.companyName || x.partnerName} [${x.companyId ?? x.partnerId ?? ''}]`
                        : x.principal_unique_id,
            },
            {
                id: 'removeMember',
                Header: '',
                accessor: (x) => (
                    <Button
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onClick={() => handleRemoveMember(x)}
                    >
                        Remove
                    </Button>
                ),
            },
        ],
        []
    );

    if (aclGroups.length === 0 && !group) {
        return <ThemedSpinner />;
    }

    if (!group) {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography color="error">Content group not found.</Typography>
                    <Button component={Link} to=".." relative="path" color="primary">
                        Back to list
                    </Button>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container direction="row" justify="center" align="center" spacing={2}>
            <Grid item xs={12}>
                <Box mt={4} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                    <Button
                        component={Link}
                        to=".."
                        relative="path"
                        startIcon={<ArrowBackIcon />}
                    >
                        Back
                    </Button>
                    <Typography variant="h2" gutterBottom style={{ margin: 0 }}>
                        {group.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {group.unique_id}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={handleOpenRenameModal}
                    >
                        Rename
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={deletingGroup}
                        onClick={handleDeleteGroup}
                    >
                        {deletingGroup ? <CircularProgress size={24} /> : 'Delete group'}
                    </Button>
                </Box>
            </Grid>

            <Grid item xs={12}>
                <ErrorMessage error={superStore.aclError} />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                    Members
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Partners and companies in this group. Add below or via Content Group Access on partner/company pages.
                </Typography>
                <Box mb={2} display="flex" flexWrap="wrap" alignItems="flex-end" gap={2}>
                    <PrincipalChooser
                        value={memberTargetName}
                        onChange={(event, { newValue }) => {
                            setMemberTargetName(newValue);
                            setMemberRequiredError(null);
                        }}
                        onSuggestionSelected={(event, { suggestion }) => {
                            setMemberTarget(suggestion);
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={addingMember || !memberTarget}
                        onClick={handleAddMember}
                    >
                        {addingMember ? <CircularProgress size={24} /> : 'Add'}
                    </Button>
                    {memberRequiredError && (
                        <Typography color="error" variant="body2">
                            {memberRequiredError}
                        </Typography>
                    )}
                </Box>
                {loadingMembers ? (
                    <CircularProgress />
                ) : members.length === 0 ? (
                    <Typography color="textSecondary">No members.</Typography>
                ) : (
                    <SimpleTable columns={memberColumns} data={members} needsFilter={members.length > 10} />
                )}
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                    Content access
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab label="Subjects" value={TAB_SUBJECTS} />
                        <Tab label="Questions" value={TAB_QUESTIONS} />
                        <Tab label="Templates" value={TAB_TEMPLATES} />
                        <Tab label="Materials" value={TAB_MATERIALS} />
                        <Tab label="Categories" value={TAB_CATEGORIES} />
                    </Tabs>
                    <FormControl variant="filled" style={{ minWidth: 200 }}>
                        <InputLabel>Filter</InputLabel>
                        <Select
                            value={currentFilter}
                            onChange={handleFilterChange(activeTab)}
                        >
                            <MenuItem value={FILTER_ALL}>All</MenuItem>
                            <MenuItem value={FILTER_DEFAULT}>Default</MenuItem>
                            <MenuItem value={FILTER_ALLOW}>Granted</MenuItem>
                            <MenuItem value={FILTER_DENY}>Denied</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Grid>

            <Grid item xs={12}>
                {selectedContentIds.size > 0 && (
                    <Box mb={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Typography variant="body2" color="textSecondary">
                            {selectedContentIds.size} selected
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={bulkLoading}
                            onClick={() => handleBulkAction('reset')}
                        >
                            {bulkLoading ? <CircularProgress size={20} /> : 'Reset'}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            disabled={bulkLoading}
                            onClick={() => handleBulkAction('allow')}
                        >
                            Allow
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            disabled={bulkLoading}
                            onClick={() => handleBulkAction('deny')}
                        >
                            Deny
                        </Button>
                    </Box>
                )}
                {isTabLoading ? (
                    <ThemedSpinner />
                ) : (
                    <SimpleTable columns={columns} data={rows} needsFilter={rows.length > 10} />
                )}
            </Grid>

            <Dialog open={renameModalOpen} onClose={() => !savingRename && setRenameModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Rename group</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="normal"
                        label="Name"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameModalOpen(false)} disabled={savingRename}>
                        Cancel
                    </Button>
                    <Button onClick={handleRename} color="primary" variant="contained" disabled={savingRename || !renameValue.trim()}>
                        {savingRename ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

export default observer(ContentGroupDetails);
